import { Component, Input, Output, EventEmitter } from '@angular/core';
import { getCidadesDisplay } from '../../../shared/utils/brasil.utils';

@Component({
  standalone: false,
  selector: 'app-fornecedor-card',
  template: `
    <article class="bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition" [class.opacity-60]="!_ativo">
      <div class="relative overflow-hidden rounded-md h-40 bg-surface mb-3">
        <img *ngIf="_thumbnail" [src]="_thumbnail.url" [alt]="_nome" class="w-full h-full object-cover" loading="lazy" />
        <div *ngIf="!_thumbnail" class="w-full h-full flex items-center justify-center text-muted">Sem foto</div>
        <div *ngIf="!_ativo" class="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded">
          Inativo
        </div>
        <div *ngIf="_publicado === false" class="absolute top-2 left-2 px-2 py-1 bg-gray-600 text-white text-xs rounded">
          Não Publicado
        </div>
      </div>
      <h3 class="text-lg font-serif font-semibold text-text-default">{{ _nome }}</h3>
      <div class="flex items-center justify-between text-sm text-muted mt-1">
        <div class="truncate">{{ _cidadesDisplay }}</div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <span class="text-amber-500">★ {{ _rating || '—' }}</span>
          <span *ngIf="_destaque" class="px-2 py-0.5 bg-primary text-white rounded-full text-xs">Destaque</span>
        </div>
      </div>
      
      <!-- Plan Level / Status Info -->
      <div class="mt-2 flex gap-1 items-center text-xs">
        <span *ngIf="_planLevel" class="px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded">
          {{ _planLevel }}
        </span>
        <span *ngIf="_leadCount !== undefined" class="px-2 py-0.5 bg-amber-100 text-amber-800 rounded">
          {{ _leadCount }} leads
        </span>
      </div>

      <div class="mt-3 flex flex-col gap-2">
        <div class="flex gap-2">
          <a *ngIf="_id" [routerLink]="['/admin/fornecedores', _id, 'preview']" class="text-sm px-3 py-1 bg-primary text-white rounded hover:bg-primary/90 flex-1 text-center">Ver</a>
          <a *ngIf="_id" [routerLink]="['/admin/fornecedores', _id, 'edit']" class="text-sm px-3 py-1 border rounded hover:bg-surface flex-1 text-center">Editar</a>
        </div>
        
        <!-- Admin Actions -->
        <div class="flex gap-2">
          <button *ngIf="_id" (click)="toggleActive()" [class.bg-green-600]="!_ativo" [class.bg-gray-600]="_ativo" class="text-sm px-3 py-1 text-white rounded hover:opacity-80 flex-1">
            {{ _ativo ? 'Desativar' : 'Ativar' }}
          </button>
          <button *ngIf="_id && _leadCount > 0" (click)="resetLeadCount()" title="Resetar contador de leads" class="text-sm px-3 py-1 border border-amber-500 text-amber-700 rounded hover:bg-amber-50">
            🔄
          </button>
          <button *ngIf="_id" (click)="deleteFornecedor()" class="text-sm px-3 py-1 bg-danger text-white rounded hover:bg-danger/80">
            🗑️
          </button>
        </div>
      </div>
    </article>
  `
})
export class FornecedorCardComponent {
  // accept either a single `fornecedor` object or individual inputs
  @Input() fornecedor?: any;
  @Input() id!: string;
  @Input() nome!: string;
  @Input() slug!: string;
  @Input() cidade?: string;
  @Input() rating?: number;
  @Input() destaque?: boolean;
  @Input() ativo?: boolean;
  @Input() publicado?: boolean;
  @Input() planLevel?: string;
  @Input() leadCount?: number;
  @Input() thumbnail?: { id?: string; url?: string; isPrimary?: boolean };

  @Output() delete = new EventEmitter<string>();
  @Output() toggleActiveEvent = new EventEmitter<string>();
  @Output() resetLeadCountEvent = new EventEmitter<string>();

  get _id() { return this.fornecedor?.id || this.id; }
  get _nome() { return this.fornecedor?.nome || this.nome; }
  get _slug() { return this.fornecedor?.slug || this.slug; }
  get _cidade() { return this.fornecedor?.cidade || this.cidade; }
  get _cidadesDisplay(): string {
    return getCidadesDisplay({ cidade: this._cidade, cidades: this.fornecedor?.cidades });
  }
  get _rating() { return this.fornecedor?.rating || this.rating; }
  get _destaque() { return this.fornecedor?.destaque || this.destaque; }
  get _ativo() { return this.fornecedor?.ativo !== undefined ? this.fornecedor.ativo : (this.ativo !== undefined ? this.ativo : true); }
  get _publicado() { return this.fornecedor?.publicado !== undefined ? this.fornecedor.publicado : this.publicado; }
  get _planLevel() { return this.fornecedor?.planLevel || this.planLevel; }
  get _leadCount() { return this.fornecedor?.leadCount !== undefined ? this.fornecedor.leadCount : this.leadCount; }
  get _thumbnail() { return this.fornecedor?.thumbnail || this.thumbnail; }

  deleteFornecedor() {
    if (!this._id) return;
    if (!confirm(`Tem certeza que deseja excluir "${this._nome}"?`)) return;
    this.delete.emit(this._id);
  }

  toggleActive() {
    if (!this._id) return;
    const action = this._ativo ? 'desativar' : 'ativar';
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} fornecedor "${this._nome}"?`)) return;
    this.toggleActiveEvent.emit(this._id);
  }

  resetLeadCount() {
    if (!this._id) return;
    if (!confirm(`Resetar contador de leads de "${this._nome}"?\n\nIsso zerará o número de leads recebidos.`)) return;
    this.resetLeadCountEvent.emit(this._id);
  }
}
